import { getCssPropertyValue, setCssPropertyValue } from './helper';

/**
 *
 * @param hex Any hex color
 * @param darkness Darkness value from 0 to 100
 * @param saturationDecrease Saturation decrease from 0 to 1
 * @returns HSL color
 * @example
 *   hexToHsl('#ff0000', 20, 0.5) // 0deg 50% 30%
 */
function hexToHsl(
  hex: string,
  darkness: number = 20,
  saturationDecrease: number = 0
): string {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, '');

  // Parse the r, g, b values
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Convert r, g, b to a range of 0 to 1
  r /= 255;
  g /= 255;
  b /= 255;

  // Find the minimum and maximum values of r, g, b
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  // Calculate the lightness
  let l = (max + min) / 2;

  let h: number = 0;
  let s: number = 0;

  if (max !== min) {
    const d = max - min;

    // Calculate the saturation
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    // Decrease the saturation by the specified percentage
    s = Math.max(0, s - s * saturationDecrease);

    // Calculate the hue
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  // Convert to HSL values
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100) - darkness;

  return `${h}deg ${s}% ${l}%`;
}

/** Manipulate elements based on their parent theme. We update the color of box-shadow elements based on the theme background color of the current component. This way we get nice tinted shadows that are neither gray nor neon. */
export const updateThemeShadows = () => {
  /** Elements that represent a theme */
  const backgroundElements: HTMLElement[] = Array.from(
    document.querySelectorAll('[class*="background"]:not(.backgroundTwo)')
  );

  backgroundElements.forEach((backgroundElement) => {
    /** Theme color (HEX) */
    const themeColor = getCssPropertyValue(
      '--theme-background-color',
      backgroundElement
    );
    if (!themeColor) return;

    /** Shadow color (HSL) */
    const shadowColor = hexToHsl(themeColor, 20, 0.5);

    const elementsWithBoxShadow = Array.from(
      backgroundElement.querySelectorAll('*')
    ).filter(
      (x: HTMLElement) => window.getComputedStyle(x).boxShadow !== 'none'
    ) as HTMLElement[];

    elementsWithBoxShadow.forEach((elementWithBoxShadow) => {
      setCssPropertyValue(
        '--shadow-color',
        shadowColor,
        elementWithBoxShadow as HTMLElement
      );
    });
  });
};

/**
 * Get the theme that is applied on {@link component}
 * @param component Any component
 * @returns The current theme background class like `"backgroundOne"` or `"backgroundTwo"`
 * @example
 *   const nextTheme = findTheme(nextComponent as HTMLElement)
 */
export const themeGetCurrent = (component: HTMLElement | Element) => {
  const theme = Array.from(component.classList).find((x) =>
    x.startsWith('background')
  );
  return theme;
};

/**
 * Remove all themes from {@link component}
 * @param component Any component
 */
export const themeClearAll = (component: HTMLElement | Element) => {
  const themes = Array.from(component.classList).filter((x) =>
    x.startsWith('background')
  );
  themes.forEach((theme) => {
    component.classList.remove(theme);
  });
};

/**
 * Apply the {@link theme} of a {@link sourceComponent} to a {@link targetComponent}
 * @param component Any component
 */
export const themeSyncFromTo = (
  sourceComponent: HTMLElement | Element,
  targetComponent: HTMLElement | Element
) => {
  const theme = themeGetCurrent(sourceComponent);
  themeClearAll(sourceComponent);
  themeClearAll(targetComponent);
  sourceComponent.classList.add(theme);
  targetComponent.classList.add(theme);
};
